import { useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { useApiFetch } from "../hooks/useApi";

function webhookBaseUrl(): string | null {
  const raw = import.meta.env.VITE_SURGEDIAL_API_BASE as string | undefined;
  if (!raw?.trim()) return null;
  return `${raw.replace(/\/$/, "")}/webhooks/wildix`;
}

type TenantOpt = { id: string; name: string; wildix_signing_configured?: boolean };

type IngestEventRow = {
  tenant_id: string | null;
  external_event_id: string;
  event_type: string;
  agent_extensions_json: string;
  caller_phone_tail: string | null;
  caller_phone_e164?: string | null;
  matched_lead_id?: number | null;
  disposition_code?: string | null;
  ingested_at: string;
};

type PageState =
  | { status: "idle" | "loading" }
  | {
      status: "ok";
      webhookRows: number;
      ingestRows: number;
      ingestLogAvailable: boolean;
      tenants: TenantOpt[];
      recentEvents: IngestEventRow[];
    }
  | { status: "err"; message: string };

export function IntegrationsPage() {
  const api = useApiFetch();
  const [page, setPage] = useState<PageState>({ status: "idle" });
  const [tenantId, setTenantId] = useState("");
  const [signingSecret, setSigningSecret] = useState("");
  const [secretSave, setSecretSave] = useState<{ status: "idle" | "saving" | "ok" | "err"; message?: string }>({
    status: "idle",
  });

  async function loadAll() {
    try {
      setPage({ status: "loading" });
      const [resW, resI, resT, resR] = await Promise.all([
        api("/api/v1/platform/webhooks/summary"),
        api("/api/v1/platform/analytics-ingest/summary"),
        api("/api/v1/tenants"),
        api("/api/v1/platform/analytics-ingest/recent?limit=20"),
      ]);
      const [textW, textI, textT, textR] = await Promise.all([
        resW.text(),
        resI.text(),
        resT.text(),
        resR.text(),
      ]);
      if (!resW.ok || !resI.ok || !resT.ok || !resR.ok) {
        const text = !resW.ok ? textW : !resI.ok ? textI : !resT.ok ? textT : textR;
        let detail = text;
        try {
          const j = JSON.parse(text) as { error?: string; reason?: string };
          detail = j.reason ?? j.error ?? text;
        } catch {
          /* raw */
        }
        setPage({ status: "err", message: detail });
        return;
      }
      const dataW = JSON.parse(textW) as { webhook_idempotency_rows?: number };
      const dataI = JSON.parse(textI) as { wildix_analytics_ingest_rows?: number; ingest_log_available?: boolean };
      const dataT = JSON.parse(textT) as { tenants?: TenantOpt[] };
      const dataR = JSON.parse(textR) as { events?: IngestEventRow[] };
      setPage({
        status: "ok",
        webhookRows: dataW.webhook_idempotency_rows ?? 0,
        ingestRows: dataI.wildix_analytics_ingest_rows ?? 0,
        ingestLogAvailable: dataI.ingest_log_available === true,
        tenants: dataT.tenants ?? [],
        recentEvents: dataR.events ?? [],
      });
      setSecretSave({ status: "idle" });
    } catch (e) {
      setPage({ status: "err", message: e instanceof Error ? e.message : String(e) });
    }
  }

  useEffect(() => {
    void loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  async function onSaveSecret(e: FormEvent) {
    e.preventDefault();
    if (!tenantId.trim()) {
      setSecretSave({ status: "err", message: "Select a tenant" });
      return;
    }
    if (!signingSecret.trim()) {
      setSecretSave({ status: "err", message: "Enter signing secret" });
      return;
    }
    try {
      setSecretSave({ status: "saving" });
      const path = `/api/v1/platform/tenants/${encodeURIComponent(tenantId.trim())}/wildix-webhook-secret`;
      const res = await api(path, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ signing_secret: signingSecret.trim() }),
      });
      const text = await res.text();
      if (!res.ok) {
        let detail = text;
        try {
          const j = JSON.parse(text) as { error?: string; detail?: string };
          detail = j.detail ?? j.error ?? text;
        } catch {
          /* raw */
        }
        setSecretSave({ status: "err", message: detail });
        return;
      }
      setSigningSecret("");
      setSecretSave({ status: "ok" });
      await loadAll();
    } catch (err) {
      setSecretSave({ status: "err", message: err instanceof Error ? err.message : String(err) });
    }
  }

  async function onClearSecret() {
    if (!tenantId.trim()) {
      setSecretSave({ status: "err", message: "Select a tenant" });
      return;
    }
    try {
      setSecretSave({ status: "saving" });
      const path = `/api/v1/platform/tenants/${encodeURIComponent(tenantId.trim())}/wildix-webhook-secret`;
      const res = await api(path, { method: "DELETE" });
      const text = await res.text();
      if (!res.ok) {
        let detail = text;
        try {
          const j = JSON.parse(text) as { error?: string };
          detail = j.error ?? text;
        } catch {
          /* raw */
        }
        setSecretSave({ status: "err", message: detail });
        return;
      }
      setSecretSave({ status: "ok" });
      await loadAll();
    } catch (err) {
      setSecretSave({ status: "err", message: err instanceof Error ? err.message : String(err) });
    }
  }

  const wildixBase = webhookBaseUrl();
  const summary = page.status === "ok" ? page : null;

  function truncateJson(s: string, max = 72): string {
    if (s.length <= max) return s;
    return `${s.slice(0, max)}…`;
  }

  return (
    <div>
      <p className="sd-kicker">Roadmap</p>
      <h1 className="sd-h1">Integrations</h1>
      <p className="sd-lead">
        Wildix PBX wizard, webhook verification, and queue-backed processing live on the Worker. Point Wildix Analytics
        (or test clients) at the webhook base below — see docs/AnalyticsWebhookExample.md for payload shape.
      </p>

      <div className="sd-main-card" style={{ marginBottom: "1rem" }}>
        <div style={{ fontSize: "0.75rem", color: "var(--sd-muted)", marginBottom: "0.35rem" }}>Webhook base (Worker)</div>
        <code style={{ fontSize: "0.8rem", wordBreak: "break-all" }}>{wildixBase ?? "—"}</code>
        {!wildixBase ? (
          <p style={{ margin: "0.75rem 0 0", fontSize: "0.85rem", color: "var(--sd-muted)" }}>
            Set <code>VITE_SURGEDIAL_API_BASE</code> at build time (GitLab CI) so this shows your staging or production
            API host.
          </p>
        ) : (
          <p style={{ margin: "0.75rem 0 0", fontSize: "0.85rem", color: "var(--sd-muted)" }}>
            Use tenant URL <code>{wildixBase}/&lt;tenant_id&gt;/…</code> for attributed ingest + optional per-tenant HMAC
            below. Legacy <code>/webhooks/wildix/v1/analytics</code> uses only the Worker secret{" "}
            <code>WILDIX_WEBHOOK_SIGNING_SECRET</code>.
          </p>
        )}
      </div>

      <div className="sd-main-card" style={{ marginBottom: "1rem" }}>
        <div style={{ fontSize: "0.75rem", color: "var(--sd-muted)", marginBottom: "0.35rem" }}>
          D1 idempotency (Platform Admin)
        </div>
        {page.status === "loading" || page.status === "idle" ? (
          <span style={{ color: "var(--sd-muted)" }}>Loading…</span>
        ) : null}
        {summary ? (
          <p style={{ margin: 0, fontSize: "1.05rem", fontWeight: 600 }}>
            {summary.webhookRows.toLocaleString()}{" "}
            <span style={{ fontWeight: 500, color: "var(--sd-muted)", fontSize: "0.9rem" }}>stored webhook keys</span>
          </p>
        ) : null}
        {page.status === "err" ? (
          <p style={{ margin: 0, color: "var(--sd-danger)", fontSize: "0.88rem" }}>{page.message}</p>
        ) : null}
      </div>

      {page.status !== "err" ? (
        <div className="sd-main-card" style={{ marginBottom: "1rem" }}>
          <div style={{ fontSize: "0.75rem", color: "var(--sd-muted)", marginBottom: "0.35rem" }}>
            D1 analytics ingest (Platform Admin)
          </div>
          {page.status === "loading" || page.status === "idle" ? (
            <span style={{ color: "var(--sd-muted)" }}>Loading…</span>
          ) : null}
          {summary ? (
            <>
              <p style={{ margin: 0, fontSize: "1.05rem", fontWeight: 600 }}>
                {summary.ingestLogAvailable ? summary.ingestRows.toLocaleString() : "—"}{" "}
                <span style={{ fontWeight: 500, color: "var(--sd-muted)", fontSize: "0.9rem" }}>ingest log rows</span>
              </p>
              {!summary.ingestLogAvailable ? (
                <p style={{ margin: "0.5rem 0 0", fontSize: "0.85rem", color: "var(--sd-muted)" }}>
                  Ingest table missing — apply D1 migration <code>0007</code> via deploy.
                </p>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      {summary && summary.tenants.length > 0 ? (
        <div className="sd-main-card" style={{ marginBottom: "1rem" }}>
          <div style={{ fontSize: "0.75rem", color: "var(--sd-muted)", marginBottom: "0.35rem" }}>
            Per-tenant Wildix webhook signing (HMAC)
          </div>
          <p style={{ margin: "0 0 0.75rem", fontSize: "0.85rem", color: "var(--sd-muted)" }}>
            When set, requests to <code>…/webhooks/wildix/&lt;tenant_id&gt;/…</code> are verified with this secret;
            otherwise the Worker <code>WILDIX_WEBHOOK_SIGNING_SECRET</code> is used. Secret is stored in D1 (
            <code>tenant_wildix_webhook_secrets</code>).
          </p>
          <form
            onSubmit={(e) => void onSaveSecret(e)}
            style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0.65rem" }}
          >
            <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem", fontSize: "0.85rem" }}>
              Tenant
              <select
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                style={inputStyle}
              >
                <option value="">—</option>
                {summary.tenants.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.id}){t.wildix_signing_configured ? " · signing set" : ""}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: "flex", flexDirection: "column", gap: "0.35rem", fontSize: "0.85rem" }}>
              Signing secret
              <input
                type="password"
                autoComplete="off"
                value={signingSecret}
                onChange={(e) => setSigningSecret(e.target.value)}
                placeholder="Wildix Analytics webhook secret"
                style={inputStyle}
              />
            </label>
            <div style={{ display: "flex", alignItems: "flex-end", gap: "0.5rem", flexWrap: "wrap" }}>
              <button type="submit" style={buttonPrimaryStyle} disabled={secretSave.status === "saving"}>
                Save secret
              </button>
              <button
                type="button"
                style={buttonMutedStyle}
                disabled={secretSave.status === "saving" || !tenantId}
                onClick={() => void onClearSecret()}
              >
                Clear tenant secret
              </button>
            </div>
          </form>
          {secretSave.status === "err" ? (
            <p style={{ margin: "0.5rem 0 0", color: "var(--sd-danger)", fontSize: "0.85rem" }}>{secretSave.message}</p>
          ) : null}
          {secretSave.status === "ok" ? (
            <p style={{ margin: "0.5rem 0 0", color: "var(--sd-muted)", fontSize: "0.85rem" }}>Saved.</p>
          ) : null}
        </div>
      ) : null}

      {summary && summary.ingestLogAvailable ? (
        <div className="sd-main-card" style={{ marginBottom: "1rem" }}>
          <div style={{ fontSize: "0.75rem", color: "var(--sd-muted)", marginBottom: "0.35rem" }}>
            Recent analytics ingest (D1)
          </div>
          {summary.recentEvents.length === 0 ? (
            <p style={{ margin: 0, color: "var(--sd-muted)", fontSize: "0.9rem" }}>No rows yet — POST a tenant webhook.</p>
          ) : (
            <div style={{ overflow: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
                <thead>
                  <tr style={{ textAlign: "left", borderBottom: "1px solid var(--sd-border)", color: "var(--sd-muted)" }}>
                    <th style={thStyle}>Ingested</th>
                    <th style={thStyle}>Tenant</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Caller tail</th>
                    <th style={thStyle}>Disposition</th>
                    <th style={thStyle}>Lead</th>
                    <th style={thStyle}>Extensions</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.recentEvents.map((ev) => (
                    <tr key={`${ev.external_event_id}-${ev.ingested_at}`} style={{ borderBottom: "1px solid var(--sd-border)" }}>
                      <td style={tdStyle}>{ev.ingested_at}</td>
                      <td style={tdStyle}>{ev.tenant_id ?? "—"}</td>
                      <td style={tdStyle}>{ev.event_type}</td>
                      <td style={tdStyle}>{ev.caller_phone_tail ?? "—"}</td>
                      <td style={tdStyle}>{ev.disposition_code ?? "—"}</td>
                      <td style={{ ...tdStyle, fontFamily: "var(--sd-mono)", fontSize: "0.75rem" }}>
                        {ev.matched_lead_id != null ? String(ev.matched_lead_id) : "—"}
                      </td>
                      <td style={{ ...tdStyle, fontFamily: "var(--sd-mono)", fontSize: "0.75rem" }}>
                        {truncateJson(ev.agent_extensions_json)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      <div className="sd-main-card" style={{ color: "var(--sd-muted)", fontSize: "0.9rem" }}>
        Counts use <code>webhook_idempotency</code> and <code>wildix_analytics_ingest_log</code> in D1 (not KV).
      </div>
    </div>
  );
}

const inputStyle: CSSProperties = {
  background: "var(--sd-surface)",
  border: "1px solid var(--sd-border)",
  borderRadius: "6px",
  padding: "0.45rem 0.6rem",
  color: "inherit",
  fontSize: "0.9rem",
};

const thStyle: CSSProperties = { padding: "0.55rem 0.65rem", fontWeight: 600 };
const tdStyle: CSSProperties = { padding: "0.45rem 0.65rem", verticalAlign: "top" };

const buttonPrimaryStyle: CSSProperties = {
  background: "var(--sd-accent)",
  color: "#04100e",
  border: "none",
  borderRadius: "8px",
  padding: "0.55rem 0.9rem",
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};

const buttonMutedStyle: CSSProperties = {
  background: "transparent",
  color: "var(--sd-muted)",
  border: "1px solid var(--sd-border)",
  borderRadius: "8px",
  padding: "0.55rem 0.9rem",
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
};
